import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  BarChart3,
  AlertTriangle,
  Plus,
  HeartPulse,
} from 'lucide-react';
import {
  mockMONAI15InstanceDetails,
  MONAIExperiment,
} from '@/mock/gpu-instance';
import {
  MONAITrainingMonitor,
  MONAIExperimentCreator,
} from '../../components/monai';

const MONAIExperimentsPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [experiments, setExperiments] = useState<MONAIExperiment[]>([]);

  const details = useMemo(() => {
    const mockDetails = mockMONAI15InstanceDetails(instanceId || '');
    if (experiments.length === 0) {
      setExperiments(mockDetails.experiments);
    }
    return mockDetails;
  }, [instanceId]);

  const handleCreateExperiment = (newExp: {
    name: string;
    task: any;
    bundle: any;
    dataset: any;
    config: any;
  }) => {
    const experiment: MONAIExperiment = {
      id: `exp-monai-${Date.now()}`,
      name: newExp.name,
      task: newExp.task,
      modality: newExp.bundle.modality,
      status: 'queued',
      bundle: newExp.bundle,
      dataset: newExp.dataset,
      config: newExp.config,
      metrics: {
        currentEpoch: 0,
        totalEpochs: newExp.config.epochs,
        trainLoss: 0,
      },
      progress: 0,
      training: {
        startedAt: 0,
        gpuUtilization: 0,
        gpuMemoryUsed: 0,
      },
      exports: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    setExperiments([experiment, ...experiments]);
  };

  // Statistics
  const trainingCount = experiments.filter((e) => e.status === 'training').length;
  const completedCount = experiments.filter((e) => e.status === 'completed').length;
  const segmentationCount = experiments.filter((e) => e.task === 'segmentation').length;
  const classificationCount = experiments.filter((e) => e.task === 'classification').length;

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-red-500" />
            MONAI Experiments
          </h1>
          <p className="text-muted-foreground">
            Medical AI training experiments with MONAI 1.5.1
          </p>
        </div>
        <Button onClick={() => setCreatorOpen(true)} className="bg-red-600 hover:bg-red-700">
          <Plus className="h-4 w-4 mr-2" />
          New Experiment
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{experiments.length}</div>
            <div className="text-sm text-muted-foreground">Total</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{trainingCount}</div>
            <div className="text-sm text-muted-foreground">Training</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-600">{completedCount}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{segmentationCount}</div>
            <div className="text-sm text-muted-foreground">Segmentation</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{classificationCount}</div>
            <div className="text-sm text-muted-foreground">Classification</div>
          </CardContent>
        </Card>
      </div>

      {/* Experiments List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Training Experiments</CardTitle>
          <CardDescription>
            Monitor your MONAI training jobs and their metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MONAITrainingMonitor
            experiments={experiments}
            onViewDetails={(exp) => console.log('View details:', exp)}
            onExport={(exp) => console.log('Export:', exp)}
          />
        </CardContent>
      </Card>

      {/* Experiment Creator Dialog */}
      <MONAIExperimentCreator
        open={creatorOpen}
        onOpenChange={setCreatorOpen}
        bundles={details.bundles}
        datasets={details.datasets}
        onCreateExperiment={handleCreateExperiment}
      />

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> These experiments are simulated for demonstration.
          In production, experiments will run on actual GPU instances using MONAI Bundle workflows.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default MONAIExperimentsPage;
